import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import {
  ensureChat,
  markRead,
  MAX_CHAT_MESSAGE_LENGTH,
  sendMessage,
  subscribeMessages,
} from "../../chat/chatService";
import {
  buildConversations,
  buildRoleTabs,
  filterConversations,
  parseTelegramChatIds,
  ROLE_TABS,
  totalsByRole,
} from "./chatAdminUtils";
import s from "./BusinessChatsPage.module.css";

export default function BusinessChatsPage() {
  const [users, setUsers] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeRole, setActiveRole] = useState(ROLE_TABS[0].role);
  const [selectedUid, setSelectedUid] = useState(null);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        setUsers(snapshot.docs.map((record) => ({
          id: record.id,
          ...record.data(),
        })));
      },
      (error) => {
        console.error("[AdminChats] Помилка завантаження користувачів:", error);
        setLoadError("Не вдалося завантажити профілі користувачів.");
      },
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "chats"),
      (snapshot) => {
        setChats(snapshot.docs.map((record) => ({
          id: record.id,
          uid: record.id,
          ...record.data(),
        })));
      },
      (error) => {
        console.error("[AdminChats] Помилка завантаження чатів:", error);
        setLoadError("Не вдалося завантажити чати.");
      },
    );

    return unsubscribe;
  }, []);

  const conversations = useMemo(
    () => buildConversations(chats, users),
    [chats, users],
  );
  const roleTabs = useMemo(
    () => buildRoleTabs(conversations),
    [conversations],
  );
  const roleTotals = useMemo(
    () => totalsByRole(conversations),
    [conversations],
  );
  const filtered = useMemo(
    () => filterConversations(conversations, activeRole, search),
    [activeRole, conversations, search],
  );
  const selected = useMemo(
    () => conversations.find((conversation) => conversation.uid === selectedUid),
    [conversations, selectedUid],
  );

  const openChat = async (conversation) => {
    setLoadError("");
    try {
      await ensureChat(conversation.uid, conversation.user);
      setSelectedUid(conversation.uid);
      await markRead(conversation.uid, "admin");
    } catch (error) {
      console.error("[AdminChats] Не вдалося відкрити чат:", error);
      setLoadError("Не вдалося відкрити чат.");
    }
  };

  return (
    <div className={s.page}>
      <TelegramNotificationSettings />

      {loadError && <div className={s.error} role="alert">{loadError}</div>}

      <div className={s.wrap}>
        <aside className={s.sidebar}>
          <div className={s.sidebarHead}>
            <div className={s.title}>Чати користувачів</div>
            <div className={s.tabs} role="tablist" aria-label="Ролі користувачів">
              {roleTabs.map(({ role, label }) => (
                <button
                  key={role}
                  type="button"
                  role="tab"
                  aria-selected={activeRole === role}
                  className={`${s.tab} ${activeRole === role ? s.tabActive : ""}`}
                  onClick={() => {
                    setActiveRole(role);
                    setSelectedUid(null);
                  }}
                >
                  <span>{label} ({roleTotals[role]?.count || 0})</span>
                  {(roleTotals[role]?.unread || 0) > 0 && (
                    <span className={s.tabBadge}>{roleTotals[role].unread}</span>
                  )}
                </button>
              ))}
            </div>
            <input
              className={s.search}
              placeholder="Пошук за іменем, email, роллю або UID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className={s.list}>
            {filtered.map((conversation) => (
              <button
                type="button"
                key={conversation.uid}
                className={`${s.item} ${
                  selectedUid === conversation.uid ? s.active : ""
                }`}
                onClick={() => openChat(conversation)}
              >
                <span className={s.meta}>
                  <span className={s.name}>{conversation.displayName}</span>
                  <span className={s.sub}>
                    {conversation.role}
                    {conversation.email ? ` · ${conversation.email}` : ""}
                  </span>
                  {conversation.lastMessage?.text && (
                    <span className={s.preview}>
                      {conversation.lastMessage.text}
                    </span>
                  )}
                </span>
                {conversation.unread > 0 && (
                  <span className={s.badge}>{conversation.unread}</span>
                )}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className={s.empty}>Для цієї ролі чатів поки немає.</div>
            )}
          </div>
        </aside>

        <main className={s.main}>
          {selected ? (
            <AdminChatWindow
              uidPartner={selected.uid}
              conversation={selected}
              key={selected.uid}
            />
          ) : (
            <div className={s.placeholder}>Оберіть чат зі списку.</div>
          )}
        </main>
      </div>
    </div>
  );
}

function TelegramNotificationSettings() {
  const [enabled, setEnabled] = useState(false);
  const [chatIds, setChatIds] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const settingsRef = doc(db, "chatSettings", "telegramNotifications");
    return onSnapshot(
      settingsRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setEnabled(data.enabled === true);
        setChatIds(Array.isArray(data.chatIds) ? data.chatIds.join("\n") : "");
        setLoading(false);
      },
      (snapshotError) => {
        console.error("[AdminChats] Помилка налаштувань Telegram:", snapshotError);
        setError("Не вдалося завантажити налаштування Telegram.");
        setLoading(false);
      },
    );
  }, []);

  const saveSettings = async () => {
    const parsed = parseTelegramChatIds(chatIds);
    setFeedback("");
    setError("");

    if (parsed.invalid.length > 0) {
      setError(`Некоректні Telegram chat ID: ${parsed.invalid.join(", ")}`);
      return;
    }
    if (enabled && parsed.valid.length === 0) {
      setError("Додайте хоча б один Telegram chat ID.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        doc(db, "chatSettings", "telegramNotifications"),
        {
          enabled,
          chatIds: parsed.valid,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null,
        },
        { merge: true },
      );
      setChatIds(parsed.valid.join("\n"));
      setFeedback("Налаштування збережено.");
    } catch (saveError) {
      console.error("[AdminChats] Помилка збереження Telegram:", saveError);
      setError("Не вдалося зберегти налаштування Telegram.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <details className={s.telegramSettings}>
      <summary>Telegram-сповіщення</summary>
      {loading ? (
        <div className={s.settingsStatus}>Завантаження...</div>
      ) : (
        <div className={s.settingsBody}>
          <label className={s.toggleLabel}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            Надсилати сповіщення про нові повідомлення
          </label>
          <label className={s.fieldLabel}>
            Telegram chat ID
            <textarea
              className={s.chatIds}
              value={chatIds}
              onChange={(event) => setChatIds(event.target.value)}
              rows={3}
              placeholder="-1001234567890"
            />
          </label>
          <div className={s.settingsActions}>
            <button
              type="button"
              className={s.saveSettings}
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? "Збереження..." : "Зберегти"}
            </button>
          </div>
          {feedback && <div className={s.success}>{feedback}</div>}
          {error && <div className={s.error} role="alert">{error}</div>}
        </div>
      )}
    </details>
  );
}

function AdminChatWindow({ uidPartner, conversation }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);
  const lastReadMessageRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeMessages(
      uidPartner,
      (list) => {
        setMsgs(list);
        setTimeout(() => {
          listRef.current?.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth",
          });
        }, 0);

        const last = list[list.length - 1];
        if (
          last?.from === "user" &&
          last.id !== lastReadMessageRef.current
        ) {
          lastReadMessageRef.current = last.id;
          markRead(uidPartner, "admin").catch((readError) => {
            console.error("[AdminChats] Помилка статусу прочитання:", readError);
          });
        }
      },
      (subscriptionError) => {
        console.error("[AdminChats] Помилка підписки на повідомлення:", subscriptionError);
        setError("Не вдалося завантажити повідомлення.");
      },
    );

    markRead(uidPartner, "admin").catch((readError) => {
      console.error("[AdminChats] Помилка статусу прочитання:", readError);
    });
    return unsubscribe;
  }, [uidPartner]);

  const onSend = async () => {
    if (!text.trim() || sending) return;

    setSending(true);
    setError("");
    try {
      await sendMessage(uidPartner, {
        text,
        from: "admin",
        senderId: auth.currentUser?.uid,
        userProfile: conversation.user,
      });
      setText("");
    } catch (sendError) {
      console.error("[AdminChats] Помилка надсилання:", sendError);
      setError("Повідомлення не надіслано. Спробуйте ще раз.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={s.chat}>
      <div className={s.chatHead}>
        <div className={s.chatTitle}>{conversation.displayName}</div>
        <div className={s.chatSub}>
          {conversation.role}
          {conversation.email ? ` · ${conversation.email}` : ""}
        </div>
      </div>

      <div className={s.chatList} ref={listRef}>
        {msgs.map((message) => (
          <div
            key={message.id}
            className={`${s.msg} ${message.from === "admin" ? s.me : s.them}`}
          >
            <div className={s.bubble}>{message.text}</div>
          </div>
        ))}
      </div>

      {error && <div className={s.chatError} role="alert">{error}</div>}

      <div className={s.inputRow}>
        <input
          className={s.input}
          value={text}
          maxLength={MAX_CHAT_MESSAGE_LENGTH}
          onChange={(event) => setText(event.target.value)}
          placeholder="Напишіть повідомлення…"
          onKeyDown={(event) => {
            if (event.key === "Enter" && text.trim() && !sending) onSend();
          }}
        />
        <button
          type="button"
          className={s.sendBtn}
          onClick={onSend}
          disabled={!text.trim() || sending}
        >
          {sending ? "Надсилання..." : "Надіслати"}
        </button>
      </div>
    </div>
  );
}
